import React from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import Marquee from "../components/sections/Marquee";
import Modalities from "../components/sections/Modalities";
import SectionDivider from "../components/SectionDivider";
import RevealOnScroll from "../components/RevealOnScroll";
import { Eyebrow, SageRule, CTA, BeginTogetherSection } from "./modalityShared";

const phases = [
    {
        accent: "#6B7F9E",
        label: "Phase one",
        title: "Understanding what you are carrying",
        body: "This is where we go deepest. We explore your history, identify the patterns that have shaped how you see yourself and the world, and begin processing the experiences that still carry weight. Sessions are weekly because this is the most focused part of the work and because you deserve consistent, dedicated support during the most tender part of the process.",
        tag: "Weekly · First 2 to 6 months",
    },
    {
        accent: "#B07090",
        label: "Phase two",
        title: "Integrating change and building resilience",
        body: "As things begin to shift, you start living the work between sessions. This phase is about deepening your trust in yourself, practicing new ways of responding, and building the tools that make those changes last. We meet less frequently because you are beginning to carry more of this on your own and that is exactly the goal.",
        tag: "Every other week, tapering to monthly",
    },
    {
        accent: "#4E7F7A",
        label: "Phase three",
        title: "Living forward with clarity and confidence",
        body: "This phase is not about maintenance, it is about becoming. We check in periodically to tend to anything that resurfaces, celebrate how far you have come, and explore new growth beyond what originally brought you here. Returning to therapy is never a step backward. It is part of living with intention and knowing yourself well enough to ask for support when it matters.",
        tag: "Quarterly, moving toward annual",
    },
];

// EMDR keeps its established dusty blue on hover. Every other modality gets a
// distinct color from the site's palette so the pills read as a subtle
// spectrum rather than five identical outlines. `hoverText` toggles ivory vs
// charcoal per fill so contrast stays legible on lighter palette values.
const modalityButtons = [
    { label: "EMDR",           href: "/emdr",           hoverFill: "#6B7F9E", hoverText: "#FAFAF8" }, // dusty blue (unchanged)
    { label: "Brainspotting",  href: "/brainspotting",  hoverFill: "#C0C1BC", hoverText: "#2C2825" }, // soft silver gray
    { label: "ACT",            href: "/act",            hoverFill: "#8A9068", hoverText: "#FAFAF8" }, // muted sage green
    { label: "DBT",            href: "/dbt",            hoverFill: "#BFA3B2", hoverText: "#2C2825" }, // dusty mauve
    { label: "EMDR Intensive", href: "/emdr-intensive", hoverFill: "#C04255", hoverText: "#FAFAF8" }, // deep rose cranberry
    // Reserve for future additions (cycles back through the palette):
    // "#4E5438" dark olive green (would pair with hoverText: "#FAFAF8")
];

const phrases = [
    "The work has a shape.",
    "Healing is not linear, but it does have direction.",
    "Every phase of this work is intentional.",
    "You are not starting over. You are going deeper.",
    "The goal is a life where fear no longer quietly shapes your direction.",
];

const beginSteps = [
    {
        n: "I",
        title: "A free consultation",
        body: "We begin with a short, pressure-free conversation to talk about what brings you here and whether working together feels like a good fit. No preparation needed. We start where you are.",
    },
    {
        n: "II",
        title: "Building the foundation",
        body: "Early sessions are about understanding your history, identifying patterns, and establishing the safety that deeper work requires. We move at a pace that feels right for you.",
    },
    {
        n: "III",
        title: "The work itself",
        body: "This is where change happens — slowly, deliberately, and with your full participation. Sessions evolve as you do. The goal is always your most integrated, grounded self.",
    },
];

const Hero = () => (
    <section data-testid="approach-hero" className="relative pt-44 pb-28 lg:pt-52 lg:pb-36" style={{ background: "#FAFAF8" }}>
        <div className="absolute top-32 left-12 hidden lg:block" aria-hidden>
            <div style={{ height: 1, width: 80, background: "#9B7D5A" }} />
            <div className="mt-3 text-gold text-sm">✦</div>
        </div>
        <div className="absolute bottom-24 right-12 hidden lg:block" aria-hidden>
            <div className="text-gold text-sm mb-3 text-right">✦</div>
            <div style={{ height: 1, width: 80, background: "#9B7D5A" }} />
        </div>
        <div className="relative mx-auto max-w-[1000px] px-6 lg:px-12 text-center" data-reveal>
            <Eyebrow>The Journey Ahead</Eyebrow>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[80px] leading-[1.02] text-charcoal text-balance">
                Where the Work <em className="italic text-gold">Takes You</em>
            </h1>
            <SageRule className="mt-8" />
            <p className="mt-10 max-w-3xl mx-auto text-lg sm:text-xl leading-[1.85] text-charcoal-soft text-pretty">
                Healing does not happen all at once, but it does have a shape. Most people move
                through three natural phases of therapy, each one building on the last. Understanding
                that arc can make the process feel less uncertain and more like something you are
                moving through with intention.
            </p>
        </div>
    </section>
);

const PhaseCard = ({ phase, idx }) => (
    <article
        data-testid={`approach-phase-${idx + 1}`}
        className="group transition-all duration-500 flex flex-col h-full hover:-translate-y-1.5"
        style={{
            background: "#FAFAF8",
            borderTop: `4px solid ${phase.accent}`,
            boxShadow: "0 1px 0 rgba(44,40,37,0.04)",
            animationDelay: `${idx * 150}ms`,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 18px 48px -28px rgba(44,40,37,0.18)")}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 0 rgba(44,40,37,0.04)")}
    >
        <div className="p-8 lg:p-10 flex-1 flex flex-col">
            <p className="text-[11px] tracking-[0.32em] uppercase font-medium" style={{ color: phase.accent }}>
                {phase.label}
            </p>
            <h3 className="mt-5 font-serif text-3xl lg:text-[34px] leading-[1.12] text-charcoal">
                {phase.title}
            </h3>
            <p className="mt-6 text-base leading-[1.85] text-charcoal-soft flex-1">{phase.body}</p>
            <div className="mt-8 pt-5" style={{ borderTop: "1px solid #E2DDD8" }}>
                <p className="text-[11px] tracking-[0.24em] uppercase" style={{ color: "#8A7968" }}>
                    {phase.tag}
                </p>
            </div>
        </div>
    </article>
);

const Phases = () => (
    <section data-testid="approach-phases" className="py-28 lg:py-36" style={{ background: "#F2EDE6" }}>
        <div className="mx-auto max-w-[1300px] px-6 lg:px-12">
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                {phases.map((p, i) => <PhaseCard key={p.label} phase={p} idx={i} />)}
            </div>
            <p className="mt-14 text-center font-serif italic text-charcoal-soft max-w-2xl mx-auto" style={{ fontSize: "15px" }}>
                Every path through this work is different. Timelines shift based on what you are
                carrying and the pace that feels right for you. We will always make those decisions together.
            </p>
        </div>
    </section>
);

const ModalityButton = ({ b }) => {
    const hoverFill = b.hoverFill || "#5C1F38";
    const hoverText = b.hoverText || "#FAFAF8";
    return (
        <a
            href={b.href}
            data-testid={`modality-pill-${b.label.toLowerCase().replace(/\s/g, "-")}`}
            className="inline-flex items-center justify-center px-7 py-3 text-[12px] tracking-[0.24em] uppercase font-medium transition-all duration-300"
            style={{
                border: "1px solid #E2DDD8",
                color: "#8A7968",
                background: "transparent",
                borderRadius: "100px",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = hoverFill;
                e.currentTarget.style.borderColor = hoverFill;
                e.currentTarget.style.color = hoverText;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "#E2DDD8";
                e.currentTarget.style.color = "#8A7968";
            }}
        >
            {b.label}
        </a>
    );
};

const ModalityPills = () => (
    <section data-testid="approach-modality-pills" className="py-28 lg:py-36" style={{ background: "#EEF2ED" }}>
        <div className="mx-auto max-w-[1100px] px-6 lg:px-12 text-center" data-reveal>
            <Eyebrow>How we do the work</Eyebrow>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                {modalityButtons.map((b) => <ModalityButton key={b.label} b={b} />)}
            </div>

            <div className="mt-16 editorial-divider"><span className="text-lg">✦</span></div>

            <div className="mt-12">
                <CTA testid="approach-pills-cta" />
            </div>
        </div>
    </section>
);

const ModalityCards = () => (
    <section data-testid="approach-modality-cards" className="py-28 lg:py-36" style={{ background: "#FAFAF8" }}>
        <div className="-my-28 lg:-my-36">
            <Modalities />
        </div>
    </section>
);

export default function ApproachPage() {
    return (
        <main className="bg-ivory text-charcoal">
            <Seo
                title="My Approach | Alchemy Practice | EMDR Therapist Wisconsin"
                description="A phased intentional approach to therapy for thoughtful adults in Wisconsin and Colorado. Learn how EMDR, Brainspotting, ACT, and DBT work together at Alchemy Practice."
            />
            <Nav />
            <RevealOnScroll>
                <Hero />
                <SectionDivider color="gold" bg="#FAFAF8" />
                <Phases />
                <SectionDivider color="sage" bg="#F2EDE6" />
                <ModalityPills />
                <SectionDivider color="rose" bg="#EEF2ED" />
                <ModalityCards />
                <Marquee phrases={phrases} />
                <SectionDivider color="gold" bg="#8A9E85" />
                <BeginTogetherSection steps={beginSteps} testidPrefix="approach" italic="care." />
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
